import { create } from 'zustand'
import type { Query, QueryClient } from '@tanstack/react-query'
import type { DashboardOverview, FolderNode } from '../domains/dashboard/models'

export type PurgeVars = { folderIds: string[]; diagramIds: string[] }

type FolderChildrenLike = {
  folders: Array<{ id: string }>
  diagrams: Array<{ id: string }>
}

export const OVERVIEW_QUERY_KEY = ['dashboard', 'overview'] as const

export type ExpandedPurgeIds = {
  folderIds: string[]
  diagramIds: string[]
}

type PurgeFilterIds = {
  folderIds: string[]
  diagramIds: string[]
}

type PurgePendingState = {
  pendingFolderIds: string[]
  pendingDiagramIds: string[]
  purgedFolderIds: string[]
  purgedDiagramIds: string[]
  pendingOperations: Array<{ operationId: string; folderId: string; status: string; phase: string; failureCode: string | null }>
  revision: number
  inFlightCount: number
  getActiveFilters: () => PurgeFilterIds
  beginPurge: () => void
  markPending: (folderIds: string[], diagramIds: string[]) => void
  releasePending: (folderIds: string[], diagramIds: string[]) => void
  commitPurged: (folderIds: string[], diagramIds: string[]) => void
  trackOperations: (operations: Array<{ operationId: string; folderId: string; status: string; phase: string; failureCode?: string | null }>) => void
  updateOperation: (operationId: string, patch: { status: string; phase: string; failureCode?: string | null }) => void
  clearOperation: (operationId: string) => void
  endPurge: () => void
}

let sanitizerInstalledFor: QueryClient | null = null

function nextRevision(revision: number): number {
  return revision + 1
}

function mergeIds(existing: string[], incoming: string[]): string[] {
  if (!incoming.length) return existing
  return Array.from(new Set([...existing, ...incoming]))
}

function subtractIds(existing: string[], remove: string[]): string[] {
  if (!remove.length) return existing
  const removeSet = new Set(remove)
  return existing.filter((id) => !removeSet.has(id))
}

function walkFolders(rootFolders: FolderNode[], fn: (node: FolderNode) => void) {
  const stack = [...rootFolders]
  while (stack.length) {
    const node = stack.pop()!
    fn(node)
    for (const child of node.children) stack.push(child)
  }
}

function findFolderById(roots: FolderNode[], id: string): FolderNode | null {
  let found: FolderNode | null = null
  walkFolders(roots, (node) => {
    if (node.id === id) found = node
  })
  return found
}

function collectDiagramIdsFromNode(node: FolderNode, diagramIds: Set<string>) {
  node.diagrams.forEach((diagram) => diagramIds.add(diagram.id))
  node.children.forEach((child) => collectDiagramIdsFromNode(child, diagramIds))
}

export function expandPurgeIds(
  vars: PurgeVars,
  overview: DashboardOverview | undefined,
): ExpandedPurgeIds {
  const diagramIds = new Set(vars.diagramIds)
  const folderIds = new Set(vars.folderIds)
  if (!overview || !vars.folderIds.length) {
    return { folderIds: [...folderIds], diagramIds: Array.from(diagramIds) }
  }

  const allRoots = [
    ...overview.personal.roots,
    ...overview.org.roots,
    ...overview.sharedWithMe.roots,
    ...overview.archived.roots,
    ...(overview.externalOrgs || []).flatMap((org) => org.roots),
  ]

  for (const folderId of vars.folderIds) {
    const folder = findFolderById(allRoots, folderId)
    if (!folder) continue
    collectDiagramIdsFromNode(folder, diagramIds)
    // Descendant folders are purged by the server's cascade too; without
    // tombstoning them, a stale children query can resurface a deleted
    // subtree until the next refetch.
    walkFolders(folder.children, (node) => folderIds.add(node.id))
  }

  return { folderIds: [...folderIds], diagramIds: Array.from(diagramIds) }
}

function filterFolderTree(
  roots: FolderNode[],
  hiddenFolders: Set<string>,
  hiddenDiagrams: Set<string>,
): FolderNode[] {
  return roots
    .filter((node) => !hiddenFolders.has(node.id))
    .map((node) => ({
      ...node,
      diagrams: node.diagrams.filter((diagram) => !hiddenDiagrams.has(diagram.id)),
      children: filterFolderTree(node.children, hiddenFolders, hiddenDiagrams),
    }))
}

export function filterOverviewForPendingPurges(
  overview: DashboardOverview,
  hiddenFolderIds: string[],
  hiddenDiagramIds: string[],
): DashboardOverview {
  if (!hiddenFolderIds.length && !hiddenDiagramIds.length) return overview

  const hiddenFolders = new Set(hiddenFolderIds)
  const hiddenDiagrams = new Set(hiddenDiagramIds)

  return {
    ...overview,
    recent: overview.recent.filter((diagram) => !hiddenDiagrams.has(diagram.id)),
    personal: { roots: filterFolderTree(overview.personal.roots, hiddenFolders, hiddenDiagrams) },
    org: { roots: filterFolderTree(overview.org.roots, hiddenFolders, hiddenDiagrams) },
    sharedWithMe: { roots: filterFolderTree(overview.sharedWithMe.roots, hiddenFolders, hiddenDiagrams) },
    archived: { roots: filterFolderTree(overview.archived.roots, hiddenFolders, hiddenDiagrams) },
    externalOrgs: overview.externalOrgs?.map((org) => ({
      ...org,
      roots: filterFolderTree(org.roots, hiddenFolders, hiddenDiagrams),
    })),
  }
}

export function filterFolderChildrenForPendingPurges<T extends FolderChildrenLike>(
  children: T,
  hiddenFolderIds: string[],
  hiddenDiagramIds: string[],
): T {
  if (!hiddenFolderIds.length && !hiddenDiagramIds.length) return children

  const hiddenFolders = new Set(hiddenFolderIds)
  const hiddenDiagrams = new Set(hiddenDiagramIds)

  return {
    ...children,
    folders: children.folders.filter((folder) => !hiddenFolders.has(folder.id)),
    diagrams: children.diagrams.filter((diagram) => !hiddenDiagrams.has(diagram.id)),
  }
}

function overviewContainsFilteredIds(
  overview: DashboardOverview,
  folderIds: string[],
  diagramIds: string[],
): boolean {
  if (!folderIds.length && !diagramIds.length) return false
  const folderSet = new Set(folderIds)
  const diagramSet = new Set(diagramIds)
  if (overview.recent.some((diagram) => diagramSet.has(diagram.id))) return true

  let found = false
  const allRoots = [
    ...overview.personal.roots,
    ...overview.org.roots,
    ...overview.sharedWithMe.roots,
    ...overview.archived.roots,
    ...(overview.externalOrgs || []).flatMap((org) => org.roots),
  ]
  walkFolders(allRoots, (node) => {
    if (found) return
    if (folderSet.has(node.id)) found = true
    if (node.diagrams.some((diagram) => diagramSet.has(diagram.id))) found = true
  })
  return found
}

function childrenContainFilteredIds(
  children: FolderChildrenLike,
  folderIds: string[],
  diagramIds: string[],
): boolean {
  if (!folderIds.length && !diagramIds.length) return false
  const folderSet = new Set(folderIds)
  const diagramSet = new Set(diagramIds)
  return (
    children.folders.some((folder) => folderSet.has(folder.id))
    || children.diagrams.some((diagram) => diagramSet.has(diagram.id))
  )
}

export function sanitizeOverviewData(overview: DashboardOverview): DashboardOverview {
  const { folderIds, diagramIds } = usePurgePendingStore.getState().getActiveFilters()
  if (!overviewContainsFilteredIds(overview, folderIds, diagramIds)) return overview
  return filterOverviewForPendingPurges(overview, folderIds, diagramIds)
}

export function sanitizeFolderChildrenData<T extends FolderChildrenLike>(children: T): T {
  const { folderIds, diagramIds } = usePurgePendingStore.getState().getActiveFilters()
  if (!childrenContainFilteredIds(children, folderIds, diagramIds)) return children
  return filterFolderChildrenForPendingPurges(children, folderIds, diagramIds)
}

export function sanitizeDashboardCaches(queryClient: QueryClient) {
  const { folderIds, diagramIds } = usePurgePendingStore.getState().getActiveFilters()
  if (!folderIds.length && !diagramIds.length) return

  const overview = queryClient.getQueryData<DashboardOverview>([...OVERVIEW_QUERY_KEY])
  if (overview && overviewContainsFilteredIds(overview, folderIds, diagramIds)) {
    queryClient.setQueryData([...OVERVIEW_QUERY_KEY], filterOverviewForPendingPurges(overview, folderIds, diagramIds))
  }

  for (const [queryKey, childData] of queryClient.getQueriesData<FolderChildrenLike>({ queryKey: ['folder'] })) {
    if (queryKey[2] !== 'children' || !childData) continue
    if (!childrenContainFilteredIds(childData, folderIds, diagramIds)) continue
    queryClient.setQueryData(queryKey, filterFolderChildrenForPendingPurges(childData, folderIds, diagramIds))
  }
}

function shouldSanitizeQuery(query: Query) {
  const queryKey = query.queryKey
  if (queryKey[0] === 'dashboard' && queryKey[1] === 'overview') return true
  return queryKey[0] === 'folder' && queryKey[2] === 'children'
}

export function installPurgeCacheSanitizer(queryClient: QueryClient) {
  if (sanitizerInstalledFor === queryClient) return
  sanitizerInstalledFor = queryClient

  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return
    if (!shouldSanitizeQuery(event.query)) return
    sanitizeDashboardCaches(queryClient)
  })
}

export function selectOverviewWithPurgeTombstones(overview: DashboardOverview): DashboardOverview {
  const { folderIds, diagramIds } = usePurgePendingStore.getState().getActiveFilters()
  return filterOverviewForPendingPurges(overview, folderIds, diagramIds)
}

export function selectFolderChildrenWithPurgeTombstones<T extends FolderChildrenLike>(children: T): T {
  const { folderIds, diagramIds } = usePurgePendingStore.getState().getActiveFilters()
  return filterFolderChildrenForPendingPurges(children, folderIds, diagramIds)
}

export const usePurgePendingStore = create<PurgePendingState>((set, get) => ({
  pendingFolderIds: [],
  pendingDiagramIds: [],
  purgedFolderIds: [],
  purgedDiagramIds: [],
  pendingOperations: [],
  revision: 0,
  inFlightCount: 0,
  getActiveFilters: () => {
    const state = get()
    return {
      folderIds: Array.from(new Set([...state.pendingFolderIds, ...state.purgedFolderIds])),
      diagramIds: Array.from(new Set([...state.pendingDiagramIds, ...state.purgedDiagramIds])),
    }
  },
  beginPurge: () => {
    set((state) => ({ inFlightCount: state.inFlightCount + 1 }))
  },
  markPending: (folderIds, diagramIds) => {
    set((state) => ({
      pendingFolderIds: mergeIds(state.pendingFolderIds, folderIds),
      pendingDiagramIds: mergeIds(state.pendingDiagramIds, diagramIds),
      revision: nextRevision(state.revision),
    }))
  },
  releasePending: (folderIds, diagramIds) => {
    set((state) => ({
      pendingFolderIds: subtractIds(state.pendingFolderIds, folderIds),
      pendingDiagramIds: subtractIds(state.pendingDiagramIds, diagramIds),
      revision: nextRevision(state.revision),
    }))
  },
  commitPurged: (folderIds, diagramIds) => {
    set((state) => ({
      purgedFolderIds: mergeIds(state.purgedFolderIds, folderIds),
      purgedDiagramIds: mergeIds(state.purgedDiagramIds, diagramIds),
      pendingFolderIds: subtractIds(state.pendingFolderIds, folderIds),
      pendingDiagramIds: subtractIds(state.pendingDiagramIds, diagramIds),
      revision: nextRevision(state.revision),
    }))
  },
  trackOperations: (operations) => {
    if (!operations.length) return
    set((state) => {
      const byId = new Map(state.pendingOperations.map((op) => [op.operationId, op]))
      let changed = false
      for (const op of operations) {
        const next = {
          operationId: op.operationId,
          folderId: op.folderId,
          status: op.status,
          phase: op.phase,
          failureCode: op.failureCode ?? null,
        }
        const prev = byId.get(op.operationId)
        if (
          !prev
          || prev.folderId !== next.folderId
          || prev.status !== next.status
          || prev.phase !== next.phase
          || prev.failureCode !== next.failureCode
        ) {
          changed = true
        }
        byId.set(op.operationId, next)
      }
      if (!changed && byId.size === state.pendingOperations.length) return state
      return {
        pendingOperations: Array.from(byId.values()),
        revision: nextRevision(state.revision),
      }
    })
  },
  updateOperation: (operationId, patch) => {
    set((state) => {
      const current = state.pendingOperations.find((op) => op.operationId === operationId)
      if (
        !current
        || (
          current.status === patch.status
          && current.phase === patch.phase
          && current.failureCode === (patch.failureCode ?? null)
        )
      ) {
        return state
      }
      return {
        pendingOperations: state.pendingOperations.map((op) =>
          op.operationId === operationId
            ? { ...op, status: patch.status, phase: patch.phase, failureCode: patch.failureCode ?? null }
            : op),
        revision: nextRevision(state.revision),
      }
    })
  },
  clearOperation: (operationId) => {
    set((state) => ({
      pendingOperations: state.pendingOperations.filter((op) => op.operationId !== operationId),
      revision: nextRevision(state.revision),
    }))
  },
  endPurge: () => {
    set((state) => ({ inFlightCount: Math.max(0, state.inFlightCount - 1) }))
  },
}))
